import { useState, useEffect, useCallback, useRef } from 'react'

const STATUS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/system-status`
const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

async function showBrowserNotification(title, body) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  try {
    const reg = await navigator.serviceWorker.ready
    await reg.showNotification(title, {
      body,
      icon: '/Sidebar logo.png',
      tag: 'system-alert',        // replaces any previous unread alert
      requireInteraction: true,   // stays until dismissed (important for urgent alerts)
    })
  } catch {
    // showNotification not supported — fall back to Notification API
    new Notification(title, { body, icon: '/Sidebar logo.png' })
  }
}

export function useSystemStatus() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const prevStatusRef = useRef(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(STATUS_URL)
      const json = await res.json()
      setData(json)

      // Detect OK → degraded transition
      if (prevStatusRef.current === 'ok' && json.status === 'degraded') {
        const failing = Object.entries(json.services || {})
          .filter(([, s]) => s.ok === false)
          .map(([name]) => name)
          .join(', ')
        await showBrowserNotification(
          '⚠️ SFP CRM System Alert',
          `Service issue detected${failing ? ': ' + failing : ''}. Tap to open dashboard.`
        )
      }
      // Detect degraded → OK (recovery)
      if (prevStatusRef.current === 'degraded' && json.status === 'ok') {
        await showBrowserNotification('✅ SFP CRM Recovered', 'All services are back online.')
      }
      prevStatusRef.current = json.status
    } catch {
      setData({ status: 'degraded', timestamp: new Date().toISOString(), services: {} })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Load status on mount
    refresh()

    // Auto-refresh every 3 minutes (180 seconds) in the background
    // User can also manually refresh anytime via the Refresh button
    const id = setInterval(refresh, 3 * 60 * 1000)
    return () => clearInterval(id)
  }, [refresh])

  return { data, loading, refresh }
}
